import type { Json } from "@/db/types";
import { getBackendClient } from "@/lib/backend";
import { profileIdOrThrow } from "@/lib/profile-id";
import type { SharedCreateTaskPayload, SharedTask } from "@/lib/types/tasks";
import { cast } from "@/lib/utils/query-helpers";

/** Error messages for each record a task can inherit its account from. */
const TASK_ACCOUNT_LINKS = {
  projects: {
    missing: "Project not found.",
    conflict: "Task account_id must match the selected project's account_id.",
  },
  gl_estimates: {
    missing: "Related estimate not found.",
    conflict: "Task account_id must match the related estimate account_id.",
  },
} as const;

/**
 * Reads the account of a record the task links to and reconciles it with the
 * account resolved so far. Three outcomes:
 *
 * - The linked record does not exist: throws the `missing` message, aborting
 *   task creation.
 * - No account resolved yet: the task inherits the linked account, and stays
 *   `null` when the linked record has no account either.
 * - An account is already resolved: it is kept, and a differing linked account
 *   is rejected with the `conflict` message rather than silently overwritten.
 *   A linked record without an account is never a conflict.
 */
async function reconcileAccountId(
  table: keyof typeof TASK_ACCOUNT_LINKS,
  recordId: string,
  currentAccountId: string | null,
): Promise<string | null> {
  const backend = getBackendClient();
  const { data, error } = await backend.data
    .from(table)
    .select("id, account_id")
    .eq("id", recordId)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error(TASK_ACCOUNT_LINKS[table].missing);
  }

  const linkedAccountId = data.account_id;
  if (!currentAccountId) {
    return linkedAccountId || null;
  }
  if (linkedAccountId && currentAccountId !== linkedAccountId) {
    throw new Error(TASK_ACCOUNT_LINKS[table].conflict);
  }
  return currentAccountId;
}

/**
 * Resolves the account a task belongs to: the payload's own account when it
 * carries a non-blank one, otherwise the account inherited from the linked
 * project and estimate, each reconciled in turn.
 */
async function resolveTaskAccountId(payload: SharedCreateTaskPayload): Promise<string | null> {
  let accountId = payload.account_id?.trim() || null;

  if (payload.project_id) {
    accountId = await reconcileAccountId("projects", payload.project_id, accountId);
  }
  if (payload.related_estimate_id) {
    accountId = await reconcileAccountId("gl_estimates", payload.related_estimate_id, accountId);
  }

  return accountId;
}

/**
 * Shapes the `xan_create_task` payload, applying the default status, priority,
 * and null fields. The seam sets `creator_id` from the caller's session.
 */
function buildTaskSeamPayload(payload: SharedCreateTaskPayload, accountId: string | null) {
  return {
    title: payload.title,
    description: payload.description ?? null,
    status: payload.status ?? "todo",
    priority: payload.priority ?? "medium",
    tags: payload.tags ?? null,
    metadata: (payload.metadata as Json | undefined) ?? null,
    project_id: payload.project_id ?? null,
    account_id: accountId,
    assignee_id: payload.assignee_id ?? null,
    related_estimate_id: payload.related_estimate_id ?? null,
    related_invoice_id: payload.related_invoice_id ?? null,
    related_po_id: payload.related_po_id ?? null,
    related_shipment_id: payload.related_shipment_id ?? null,
    due_date: payload.due_date?.trim() || null,
  };
}

/**
 * Creates a task attributed to the authenticated profile through the
 * idempotent `xan_create_task` seam, inheriting the account from the linked
 * project or estimate when the payload omits one. Retrying the same
 * `commandId` with the same payload returns the first task, never a second.
 */
export async function createTaskRecord(
  commandId: string,
  payload: SharedCreateTaskPayload,
  profileId: string | null,
): Promise<SharedTask> {
  // The seam attributes the task to the session's profile; without one,
  // refuse before sending a read or write.
  profileIdOrThrow(profileId);
  const accountId = await resolveTaskAccountId(payload);

  const backend = getBackendClient();
  const { data, error } = await backend.data.rpc<SharedTask>("xan_create_task", {
    p_command_id: commandId,
    p_payload: buildTaskSeamPayload(payload, accountId),
  });

  if (error) {
    throw new Error(error.message);
  }

  return cast<SharedTask>(data);
}
